import { TabData } from '@/models/tab-data';
import { TabHost } from '@/models/tab-registry';
import { Orientation, Tab, TabStore } from '@/state/tab.store';
import { ComponentPortal } from '@angular/cdk/portal';
import { Injectable, Injector, inject } from '@angular/core';
import { v4 as uuid } from 'uuid';

@Injectable({
  providedIn: 'root',
})
export class TabService {
  private readonly injector = inject(Injector);
  private readonly state = inject(TabStore);

  /**
   * Builds a tab without touching the store. Restoring passes the stored id so that `activeTabId`
   * keeps naming something real; everything else lets it mint a fresh one.
   *
   * `data` is persisted, so callers must pass plain JSON.
   */
  createTab<T>(type: TabHost, title: string, icon: string, data?: T, id: string = uuid()): Tab {
    const injector = Injector.create({
      parent: this.injector,
      providers: [{ provide: TabData, useValue: new TabData(data) }],
    });

    return {
      id,
      icon,
      title,
      content: new ComponentPortal(type, null, injector),
      componentKey: type.tabKey,
      data,
    };
  }

  open<T>(type: TabHost, title: string, icon: string, data?: T, split?: Orientation) {
    const tab = this.createTab(type, title, icon, data);

    if (!split) {
      this.state.addTabToActiveGroup(tab);
    } else {
      this.state.addAndSplitTab(tab, split);
    }
  }
}
